import { EventEmitter, Injectable } from '@angular/core';
import { catchError, concatMap, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MessageService } from './message.service';
import { Channel } from '../models/channel';
import { Message } from '../models/message';
import { map, tap } from 'rxjs/operators';
import { ConfigLoaderService } from '../config/configloader.service';
import { CommActionItem } from '../interfaces/comm-action-item';
import { IssueState } from '../models/enums/issueStates';

interface SaveMessageRequest {
  message: Message;
  membersIDs: string[];
}

/**
 * this class represents access to all rest requests concerning Channel data of the PyWall
 * this rest service honors isMock from settings service
 */
@Injectable()
export class ChannelService {

  public messageEventUpdater = new EventEmitter<string>();
  private projectBaseURLChannels: string;
  private projectBaseURLMessages: string;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private configService: ConfigLoaderService,
  ) {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      await this.initializeUrls();
    } catch (error) {
      console.error('Failed to initialize URLs:', error);
    }
  }

  private async initializeUrls() {
    try {
      const settings = this.configService.settings || await this.configService.loadSettings();
      this.projectBaseURLChannels = `${settings.pyWallServer.url}channels`;
      this.projectBaseURLMessages = `${settings.pyWallServer.url}messages`;
    } catch (error) {
      console.error('Failed to load settings', error);
    }
  }

  create(projectKey: String, membersIds: string[]): Observable<Channel> {
    this.messageService.sendDebug('ChannelService create called.');
    return this.http.post<Channel>(`${this.projectBaseURLChannels}/`, { project_key: projectKey, members: membersIds });
  }

  getChannelByProjectKey(projectKey: string, membersIds: string[]): Observable<Channel> {
    this.messageService.sendDebug('ChannelService getByProjectKey called.');
    const targetURL = this.projectBaseURLChannels + `/${projectKey}/`;

    return this.http.get<Channel>(targetURL).pipe(
      catchError((error: any) => {
        if (error.status === 404) {
          this.messageService.sendDebug('Channel not found, creating new one.');
          return this.create(projectKey, membersIds);
        } else {
          return throwError(() => new Error(error));
        }
      }),
    );
  }

  getUnreadMessages(projectKey: string, userId: string): Observable<number> {
    const targetURL = `${this.projectBaseURLMessages}/${projectKey}/${userId}/unread`;
    return this.http.get<number>(targetURL);
  }

  getMessagesByChannelId(channelId: number, userId: string): Observable<Message[]> {
    const targetURL = `${this.projectBaseURLMessages}/${channelId}/${userId}`;

    return this.http.get<any[]>(targetURL).pipe(
      map(messages => messages.map(msg => Message.fromJSON(msg))), // Convert raw objects into Message instances
      catchError(error => {
        console.error('Error fetching messages:', error);
        return throwError(() => error);
      })
    );
  }


  getActionItemsByChannelId(projectKey: string): Observable<{ actionItems: CommActionItem[] }> {
    const targetURL = `${this.projectBaseURLChannels}/${projectKey}/action-items`;
    return this.http.get<{ action_items: any[] }>(targetURL).pipe(
      map(response => {
        const action_items = response.action_items;

        const actionItems: CommActionItem[] = action_items.map(issue => ({
          id: issue.id,
          title: issue.title,
          description: issue.description,
          actionItemType: issue.actionItemType,
          issueType: issue.issueType,
          timestampTriggered: issue.timestampTriggered ? new Date(issue.timestampTriggered) : null,
          timestampSuggested: issue.timestampSuggested ? new Date(issue.timestampSuggested) : null,
          timestampProcessed: issue.timestampProcessed ? new Date(issue.timestampProcessed) : null,
          state: issue.state as IssueState,
          issueId: issue.issueId,
          parentActionItemId: issue.parentActionItemId,
        }));
        return { actionItems };
      }),
    );
  }


  saveMessage(message: Message, membersIDs: string[]): Observable<Message> {
    const requestBody: SaveMessageRequest = {
      message: message,
      membersIDs: membersIDs,
    };
    return this.http.post<Message>(`${this.projectBaseURLChannels}/${message.channelId}/messages`, requestBody);
  }

  updateIsRead(messageId: number, userId: string): Observable<void> {
    this.messageService.sendDebug('ChannelService updateIsRead called.');
    const targetURL = `${this.projectBaseURLMessages}/${messageId}/${userId}`;
    return this.http.patch<void>(targetURL, {}).pipe(
      catchError((error: any) => {
        console.error('Error in updateIsRead:', error);
        return throwError(() => new Error(error));
      }),
      tap(() => this.messageEventUpdater.emit('message_read')),
    );
  }

  updateActionItemId(issueId: number): Observable<Message> {
    const targetURL = `${this.projectBaseURLMessages}/${issueId}`;
    return this.http.patch<{ message: Message }>(targetURL, {}).pipe(
      catchError((error: any) => {
        console.error('Error in updateIssueId:', error);
        return throwError(() => new Error(error));
      }),
      map(response => response.message),
    );
  }

  updateActionItem(actionItemId: number, payload: Partial<CommActionItem>): Observable<CommActionItem> {
    console.log('IN UPDATE actionItem SERVICE: ', actionItemId);
    const targetURL = `${this.projectBaseURLChannels}/issue-suggestions/${actionItemId}`;
    return this.http.patch<CommActionItem>(targetURL, payload).pipe(
      map(response => {
        if (response) {
          return response;
        } else {
          throw new Error('Failed to update issue suggestions');
        }
      }),
    );
  }

  updateActionItemByIssueId(issueId: number, payload: Partial<CommActionItem>): Observable<CommActionItem> {
    console.log('IN UPDATE actionItem SERVICE: ', issueId);
    const targetURL = `${this.projectBaseURLChannels}/issue-suggestions/by-issue/${issueId}`;
    return this.http.patch<CommActionItem>(targetURL, payload).pipe(
      map(response => {
        if (response) {
          return response;
        } else {
          throw new Error('Failed to update issue suggestions');
        }
      }),
      catchError(error => {
        if (error.status === 404) {
          console.warn(`No action item found for issueId ${issueId}`);
          return of(null);
        }
        return throwError(() => error);
      }),
    );
  }

  updateIsAnalyzed(messages: Message[]): Observable<Message[]> {
    const payload = { messages };
    const targetURL = `${this.projectBaseURLMessages}`;
    return this.http.patch<any>(targetURL, payload).pipe(
      map(response => {
        if (response.status === 'success') {
          return response.data.messages;
        } else {
          throw new Error('Failed to update messages');
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Update failed:', error.message);
        return throwError(() => new Error('Update failed: ' + error.message));
      }),
    );
  }
}
